import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { COLOR_RESULTADO, n, nombrePublicacion, plural, sincronizarEnLotes, type Progreso, type Resultado } from '../lib/sincronizarEnLotes';

/**
 * "Combos sin sincronizar" (2026-11-02). Un combo de varios SKU (no un pack) que no sincroniza deja sus publicaciones con el
 * stock viejo en Mercado Libre: la sincronización las saltea en silencio porque dependen de un SKU no habilitado. Lista
 * paginada y filtrable (sin leer ML) con lo que le falta a cada combo; se sincronizan de a muchos sólo los listos (todos sus
 * componentes ya sincronizan y están contados), de a lotes de 100 con el reintento ante el límite de pedidos, igual que
 * "Packs sin sincronizar". Sólo admin; el resto ve la lista.
 */

const URL = '/api/v3/ml/sync-sku';
const POR_PAGINA = 50;
/** Cuántos de los cambios más grandes muestra la confirmación. */
const SALTOS_A_MOSTRAR = 5;

interface PublicacionCombo {
  mlaId: string;
  variantId: string | null;
  titulo: string | null;
  stockMl: number | null;
  sePublica: number | null;
  seEscribe: boolean;
  motivo: string | null;
  statusMl: string | null;
  pausadaEnMl: boolean;
  aviso: string | null;
}

interface FilaCombo {
  sku: string;
  descripcion: string | null;
  marca: string | null;
  componentes: Array<{ sku: string; descripcion: string | null; unidades: number; habilitado: boolean; contado: boolean }>;
  faltaSincronizar: string[];
  faltaContar: string[];
  listo: boolean;
  sePublica: number | null;
  publicacionesActivas: number;
  publicacionesQueSeEscriben: number;
  publicaciones: PublicacionCombo[];
  avisos: string[];
}

interface ComboListo {
  sku: string;
  componentes: string[];
  publicacionesQueSeEscriben: number;
  mayorCambio: { mlaId: string; stockMl: number | null; sePublica: number | null } | null;
}

interface Listado { total: number; pagina: number; porPagina: number; filas: FilaCombo[]; listos: ComboListo[] }

interface ResultadoLote { resultados: Array<{ sku: string; estado: 'ok' | 'revisar' | 'error'; mensaje: string | null }> }
const salto = (c: ComboListo) => Math.abs((c.mayorCambio?.sePublica ?? 0) - (c.mayorCambio?.stockMl ?? 0));
const SOLO_ADMIN = 'Sólo un administrador puede sincronizarlos';

export function CombosSinSincronizar({ esAdmin }: { esAdmin: boolean }) {
  const [listado, setListado] = useState<Listado | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [marcaElegida, setMarcaElegida] = useState('');
  const [filtro, setFiltro] = useState({ q: '', marca: '' });
  const [marcas, setMarcas] = useState<string[]>([]);
  const [pagina, setPagina] = useState(1);
  /** Combos listos elegidos con lo que se confirma (sus componentes). */
  const [elegidos, setElegidos] = useState<Map<string, ComboListo>>(new Map());
  const [confirmar, setConfirmar] = useState(false);
  const [progreso, setProgreso] = useState<Progreso | null>(null);
  const [resultados, setResultados] = useState<Record<string, Resultado>>({});
  const [resumen, setResumen] = useState<string | null>(null);
  const corriendo = progreso !== null;
  const montado = useRef(true);
  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);

  useEffect(() => {
    api.get<{ marcas: string[] }>(`${URL}/opciones-filtro`).then((o) => setMarcas(o?.marcas ?? [])).catch(() => {});
  }, []);

  /** Número del último pedido: la respuesta de uno anterior se descarta. */
  const ultimoPedido = useRef(0);
  const cargar = useCallback(async () => {
    const pedido = ++ultimoPedido.current;
    setCargando(true);
    try {
      const params = new URLSearchParams({
        ...(filtro.q ? { q: filtro.q } : {}), ...(filtro.marca ? { marca: filtro.marca } : {}), pagina: String(pagina), porPagina: String(POR_PAGINA),
      });
      const r = await api.get<Listado>(`${URL}/combos-sin-sincronizar?${params}`);
      if (pedido !== ultimoPedido.current) return;
      // La lista se achicó (p. ej. después de sincronizar): se pide la última página.
      const ultima = Math.max(1, Math.ceil(r.total / r.porPagina));
      if (r.total > 0 && pagina > ultima) { setPagina(ultima); return; }
      setListado(r);
      setError(null);
    } catch (e) {
      if (pedido === ultimoPedido.current) setError(extractErrorMessage(e));
    } finally {
      if (pedido === ultimoPedido.current) setCargando(false);
    }
  }, [filtro, pagina]);

  useEffect(() => { void cargar(); }, [cargar]);

  const filtrar = (e: FormEvent) => {
    e.preventDefault();
    setPagina(1);
    setFiltro({ q: texto.trim(), marca: marcaElegida });
  };

  const alternar = (sku: string) => setElegidos((previos) => {
    const siguientes = new Map(previos);
    const info = listado?.listos.find((c) => c.sku === sku);
    if (siguientes.has(sku)) siguientes.delete(sku);
    else if (info) siguientes.set(sku, info);
    return siguientes;
  });
  const elegirTodos = () => setElegidos(new Map((listado?.listos ?? []).map((c) => [c.sku, c])));

  const aSincronizar = [...elegidos.values()];
  const publicacionesAEscribir = aSincronizar.reduce((t, c) => t + c.publicacionesQueSeEscriben, 0);
  const saltos = aSincronizar.filter((c) => c.mayorCambio).sort((a, b) => salto(b) - salto(a)).slice(0, SALTOS_A_MOSTRAR);

  /** Cada combo con sus componentes confirmados, de a lotes (sincronizarEnLotes). */
  const sincronizar = async () => {
    if (corriendo || aSincronizar.length === 0) return;
    setConfirmar(false);
    setResumen(null);
    setResultados({});
    const hecho = await sincronizarEnLotes(
      aSincronizar,
      (items) => api.post<ResultadoLote>(`${URL}/habilitar-combos`, { items: items.map((c) => ({ sku: c.sku, componentes: c.componentes })) }),
      { seguir: () => montado.current, progreso: setProgreso, resultados: setResultados },
    );
    if (hecho === null) return;
    setProgreso(null);
    setElegidos(new Map());
    setResumen(hecho);
    await cargar();
  };

  const filas = listado?.filas ?? [];
  const paginas = listado ? Math.max(1, Math.ceil(listado.total / listado.porPagina)) : 1;
  // Los combos que empezaron a sincronizar salen de la lista: la columna sólo se muestra si queda alguna fila con resultado.
  const conResultados = filas.some((f) => resultados[f.sku]);
  const columnas = (esAdmin ? 1 : 0) + 4 + (conResultados ? 1 : 0);
  const titulo = 'Combos sin sincronizar';

  return (
    <section aria-label={titulo} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-800">{titulo}</h3>
          {listado && <Badge tone={listado.total > 0 ? 'amber' : 'green'}>{plural(listado.total, 'combo', 'combos')}</Badge>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => void cargar()} loading={cargando} disabled={corriendo}>Actualizar</Button>
      </div>
      <p className="text-sm text-slate-600 max-w-4xl">
        Combos de varios productos que todavía no sincronizan. Mientras no los sincronices, Mercado Libre se queda con el stock
        viejo de sus publicaciones. Se pueden sincronizar los que dicen &quot;Listo para sincronizar&quot;: todos sus componentes
        ya sincronizan y están contados. A los demás primero hay que sincronizarles o contarles lo que dice cada uno.
        Sincronizar escribe sólo el stock: una publicación pausada sigue pausada hasta que la reactives en Mercado Libre.
      </p>

      <form onSubmit={filtrar} className="flex flex-wrap items-end gap-2">
        <input
          type="search"
          aria-label="Buscar combo"
          placeholder="Combo o descripción"
          className="w-64 rounded border border-slate-300 px-2 py-1 text-sm"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <label className="text-xs text-slate-600">
          Marca
          <select aria-label="Marca" value={marcaElegida} onChange={(e) => setMarcaElegida(e.target.value)}
            className="block max-w-[12rem] rounded border border-slate-300 px-2 py-1 text-sm">
            <option value="">Todas</option>
            {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <Button type="submit" variant="secondary" size="sm" disabled={corriendo}>Filtrar</Button>
        {esAdmin && listado && listado.listos.length > 0 && (
          <Button type="button" variant="ghost" size="sm" onClick={elegirTodos} disabled={corriendo}>
            {listado.listos.length === 1 ? 'Elegir el combo listo filtrado' : `Elegir los ${n(listado.listos.length)} combos listos filtrados`}
          </Button>
        )}
        {esAdmin && elegidos.size > 0 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setElegidos(new Map())} disabled={corriendo}>Quitar la selección</Button>
        )}
        <Button type="button" size="sm" onClick={() => setConfirmar(true)} disabled={!esAdmin || corriendo || elegidos.size === 0}
          title={esAdmin ? undefined : SOLO_ADMIN}>
          {`Sincronizar ${plural(elegidos.size, 'combo', 'combos')}`}
        </Button>
      </form>

      {error && <p role="alert" className="text-sm text-red-600">No se pudo cargar la lista: {error}</p>}

      {progreso && (
        <p role="status" className="text-sm text-slate-700">
          <span>{`Sincronizando lote ${progreso.actual} de ${progreso.total}…`}</span>
          {progreso.esperando && <span className="block text-amber-700">Esperando para seguir (límite de pedidos)…</span>}
          <span className="block text-xs text-slate-500">No cierres esta pantalla hasta que termine.</span>
        </p>
      )}
      {resumen && (
        <p role="status" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-700">{`Listo: ${resumen}.`}</p>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              {esAdmin && <th className="py-1 px-2 font-medium"><span className="sr-only">Elegir</span></th>}
              <th className="py-1 px-2 font-medium">Combo</th>
              <th className="py-1 px-2 font-medium">Componentes</th>
              <th className="py-1 px-2 font-medium">Estado</th>
              <th className="py-1 px-2 font-medium" title="Stock en Mercado Libre según la última sincronización de publicaciones → lo que se publicaría">
                Publicaciones (hoy en Mercado Libre → se publicaría)
              </th>
              {conResultados && <th className="py-1 px-2 font-medium">Resultado</th>}
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr><td colSpan={columnas} className="py-3 px-2 text-slate-500">{cargando ? 'Cargando…' : 'No hay combos sin sincronizar'}</td></tr>
            )}
            {filas.map((f) => {
              const r = resultados[f.sku];
              return (
                <tr key={f.sku} className="border-b border-slate-100 align-top">
                  {esAdmin && (
                    <td className="py-1 px-2">
                      <input
                        type="checkbox"
                        aria-label={`Elegir ${f.sku}`}
                        checked={elegidos.has(f.sku)}
                        disabled={corriendo || !f.listo}
                        title={f.listo ? undefined : 'Todavía no está listo: mirá qué le falta'}
                        onChange={() => alternar(f.sku)}
                      />
                    </td>
                  )}
                  <td className="py-1 px-2">
                    <div className="font-medium">{f.sku}</div>
                    <div className="text-xs text-slate-500">{f.descripcion ?? '—'}</div>
                    {f.marca && <div className="text-xs text-slate-500">{f.marca}</div>}
                  </td>
                  <td className="py-1 px-2 text-xs">
                    <ul className="space-y-0.5">
                      {f.componentes.map((c) => (
                        <li key={c.sku} title={c.descripcion ?? undefined} className={c.habilitado && c.contado ? '' : 'text-amber-700'}>
                          {`${c.sku} x${c.unidades.toLocaleString('es-AR')}`}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-1 px-2 text-xs">
                    {f.listo ? <Badge tone="green">Listo para sincronizar</Badge> : (
                      <ul className="space-y-0.5 text-amber-700">
                        {f.faltaSincronizar.length > 0 && <li>{`Falta sincronizar: ${f.faltaSincronizar.join(', ')}`}</li>}
                        {f.faltaContar.length > 0 && <li>{`Falta contar: ${f.faltaContar.join(', ')}`}</li>}
                      </ul>
                    )}
                    {f.avisos.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-slate-500">{f.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
                    )}
                  </td>
                  <td className="py-1 px-2">
                    <ul className="space-y-0.5 text-xs">
                      {f.publicaciones.map((p) => (
                        <li key={`${p.mlaId}-${p.variantId ?? ''}`} title={p.titulo ?? undefined}>
                          <span className={p.seEscribe ? '' : 'text-amber-700'}>
                            {p.seEscribe
                              ? `${nombrePublicacion(p)}: Hoy en Mercado Libre ${n(p.stockMl)} → se publicaría ${n(p.sePublica)}`
                              : `${nombrePublicacion(p)}: No se actualiza — ${p.motivo ?? ''}`}
                          </span>
                          {p.aviso && <span className="block text-amber-700">{p.aviso}</span>}
                        </li>
                      ))}
                    </ul>
                  </td>
                  {conResultados && <td className={`py-1 px-2 text-xs ${r ? COLOR_RESULTADO[r.estado] : ''}`}>{r?.mensaje ?? ''}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {listado && paginas > 1 && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Button variant="ghost" size="sm" onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={pagina <= 1 || corriendo}>Anterior</Button>
          <span>{`Página ${pagina} de ${paginas}`}</span>
          <Button variant="ghost" size="sm" onClick={() => setPagina((p) => Math.min(paginas, p + 1))} disabled={pagina >= paginas || corriendo}>Siguiente</Button>
        </div>
      )}

      <Modal open={confirmar} onClose={() => setConfirmar(false)} title="Sincronizar combos" size="md">
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            {`Empiezan a sincronizar ${plural(aSincronizar.length, 'combo', 'combos')}: `}
            <strong>{`se va a escribir en Mercado Libre el stock de ${plural(publicacionesAEscribir, 'publicación', 'publicaciones')}`}</strong>.
          </p>
          {saltos.length > 0 && (
            <div>
              <p>Los cambios más grandes:</p>
              <ul className="list-disc pl-5 text-slate-600">
                {saltos.map((c) => (
                  <li key={c.sku}>{`${c.sku} · ${nombrePublicacion(c.mayorCambio!)}: hoy en Mercado Libre ${n(c.mayorCambio!.stockMl)} → ${n(c.mayorCambio!.sePublica)}`}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-slate-600">
            El stock se escribe en los próximos minutos, de a una publicación. Las que dicen &quot;No se actualiza&quot; quedan con el
            stock viejo hasta corregir lo que dice cada una. Las pausadas siguen pausadas: hay que reactivarlas en Mercado Libre.
          </p>
          <div className="flex justify-end gap-3 pt-3 border-t">
            <Button variant="ghost" onClick={() => setConfirmar(false)}>Cancelar</Button>
            <Button onClick={() => void sincronizar()}>Confirmar y sincronizar</Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
